import { useEffect, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, AlertTriangle, CheckCircle2, XCircle,
  Eye, Mail, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchAdminErrorReports,
  triageErrorReport,
} from "../lib/api";

const TYPE_LABEL = {
  WRONG_RISK_LEVEL:    "Wrong risk level",
  WRONG_INTERACTION:   "Wrong interaction",
  MISSING_INTERACTION: "Missing interaction",
  OTHER:               "Other",
};

const STATUS_TONE = {
  NEW:       "bg-amber-50 border-amber-200 text-amber-800",
  REVIEWING: "bg-blue-50 border-blue-200 text-blue-800",
  RESOLVED:  "bg-emerald-50 border-emerald-200 text-emerald-800",
  REJECTED:  "bg-red-50 border-red-200 text-red-800",
};

function StatTile({ label, value, tone }) {
  const TONES = {
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    blue:   "bg-blue-50 border-blue-200 text-blue-900",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-900",
    red:    "bg-red-50 border-red-200 text-red-900",
  };
  return (
    <div
      className={`rounded-xl border p-3 text-center ${TONES[tone]}`}
      data-testid={`errors-summary-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function ErrorCard({ rep, onTriaged }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const triage = async (status) => {
    setBusy(true);
    try {
      await triageErrorReport(rep.id, { status, resolution_notes: notes || undefined });
      toast.success(`Marked ${status.toLowerCase()}`);
      onTriaged(rep.id, status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Triage failed");
    } finally {
      setBusy(false);
    }
  };

  const intx = rep.interaction;
  const med = intx?.medication;
  const ing = intx?.ingredient;

  return (
    <article
      data-testid={`error-card-${rep.id}`}
      className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-stone-100 bg-stone-50/70 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-stone-700">
          <AlertTriangle className="h-3 w-3" />
          {TYPE_LABEL[rep.report_type] || rep.report_type}
        </span>
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_TONE[rep.status] || ""}`}>
          {rep.status}
        </span>
        {rep.reporter_email && (
          <span className="inline-flex items-center gap-1 text-[11px] text-stone-600">
            <Mail className="h-3 w-3 text-stone-400" /> {rep.reporter_email}
          </span>
        )}
        <span className="ml-auto text-[11px] text-stone-400 font-mono">
          {new Date(rep.reported_at).toLocaleDateString()}
        </span>
      </div>

      <div className="px-5 py-4 space-y-3">
        <p className="text-sm text-stone-800 leading-relaxed">{rep.description}</p>

        {/* Linked interaction context */}
        {intx && (
          <div className="rounded-xl border border-stone-200 bg-stone-50/40 p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1.5">
              Linked interaction
            </div>
            <div className="text-sm text-stone-800">
              <span className="font-semibold">{med?.generic_name || "—"}</span>
              <span className="text-stone-400 mx-2">×</span>
              <span className="font-semibold">{ing?.name || "—"}</span>
              <span className="ml-2 text-[11px] inline-flex items-center gap-1 rounded-full border border-stone-300 px-2 py-0.5 text-stone-600 uppercase">
                {intx.risk_level}
              </span>
            </div>
            {intx.what_happens_plain && (
              <p className="text-xs text-stone-600 mt-1.5 leading-snug">{intx.what_happens_plain}</p>
            )}
          </div>
        )}

        {rep.product_link && (
          <div className="text-xs text-stone-600">
            <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Linked product:</span>{" "}
            {rep.product_link.full_name}
          </div>
        )}

        {rep.resolution_notes && (
          <div className="rounded-xl border border-stone-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">
              Triage notes
            </div>
            <p className="text-xs text-stone-700">{rep.resolution_notes}</p>
            {rep.reviewed_by && (
              <p className="text-[10px] text-stone-400 mt-1">— {rep.reviewed_by}, {rep.reviewed_date}</p>
            )}
          </div>
        )}
      </div>

      {/* Triage actions */}
      {rep.status !== "RESOLVED" && rep.status !== "REJECTED" && (
        <>
          <div className="border-t border-stone-100 bg-stone-50 px-5 py-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Resolution notes (optional)
              </span>
              <input
                data-testid={`error-notes-${rep.id}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reviewed against FDA label; rule will be tightened…"
                className="mt-1 w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
              />
            </label>
          </div>
          <div className="flex items-center gap-2 px-5 py-3 border-t border-stone-100 flex-wrap">
            {rep.status === "NEW" && (
              <Button
                variant="outline"
                onClick={() => triage("REVIEWING")}
                disabled={busy}
                data-testid={`error-reviewing-${rep.id}`}
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                Mark reviewing
              </Button>
            )}
            <Button
              onClick={() => triage("RESOLVED")}
              disabled={busy}
              data-testid={`error-resolve-${rep.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Resolve
            </Button>
            <Button
              variant="ghost"
              onClick={() => triage("REJECTED")}
              disabled={busy}
              data-testid={`error-reject-${rep.id}`}
              className="text-red-600 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </div>
        </>
      )}
    </article>
  );
}

export default function AdminErrors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminErrorReports({
        limit: 200,
        status: statusFilter || undefined,
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const onTriaged = (id, status) => {
    setData((prev) => prev ? {
      ...prev,
      items: prev.items.map((r) => r.id === id ? { ...r, status } : r),
    } : prev);
    setTimeout(reload, 200);
  };

  const summary = data?.summary || { new: 0, reviewing: 0, resolved: 0, rejected: 0 };

  return (
    <main
      data-testid="admin-errors-page"
      className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12"
    >
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <header className="mt-5 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Error Reports</h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
              Triage user-reported catalog issues. Mark each report Reviewing, Resolved, or Rejected.
              Resolved reports help feed the verification queue.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-wider text-stone-600">
            <ShieldCheck className="h-3 w-3" /> Admin
          </span>
        </header>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <StatTile label="New"       value={summary.new}       tone="amber" />
          <StatTile label="Reviewing" value={summary.reviewing} tone="blue" />
          <StatTile label="Resolved"  value={summary.resolved}  tone="green" />
          <StatTile label="Rejected"  value={summary.rejected}  tone="red" />
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-stone-500">Filter by status:</span>
          {["", "NEW", "REVIEWING", "RESOLVED", "REJECTED"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`errors-filter-${(s || "all").toLowerCase()}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === s
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
              }`}
            >
              {s || "All"}
            </button>
          ))}
          <button
            onClick={reload}
            data-testid="errors-refresh"
            className="ml-auto text-xs text-stone-500 hover:text-stone-900 px-2 py-1.5"
          >
            Refresh
          </button>
        </div>

        {/* Queue */}
        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : (data?.items?.length ?? 0) === 0 ? (
          <div
            data-testid="errors-empty-state"
            className="rounded-2xl border border-dashed border-stone-200 bg-white p-12 text-center"
          >
            <ShieldCheck className="h-10 w-10 text-emerald-400 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-stone-700">No reports here.</p>
            <p className="mt-1 text-xs text-stone-500">When users flag bad data they'll show up here for triage.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="errors-list">
            {data.items.map((rep) => (
              <ErrorCard key={rep.id} rep={rep} onTriaged={onTriaged} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
